/** Line states the warehouse can still inbound from, and the wider set it can still cancel. */
export const INBOUNDABLE_LINE_STATUSES = ['ORDERED', 'RESERVED', 'AWAITING_INBOUND'];
export const CANCELLABLE_LINE_STATUSES = ['ORDERED', 'RESERVED', 'AWAITING_INBOUND', 'INBOUNDED', 'READY'];
export const OUTBOUND_READY_STATUSES = ['INBOUNDED', 'READY'];
/** States whose arrival can still be taken back; a fulfillment status recalc may already have promoted INBOUNDED to READY. */
export const INBOUND_REVERSIBLE_LINE_STATUSES = ['INBOUNDED', 'READY'];

export const canInboundLine = (lineStatus: string): boolean => INBOUNDABLE_LINE_STATUSES.includes(lineStatus);

export const canCancelLine = (lineStatus: string): boolean => CANCELLABLE_LINE_STATUSES.includes(lineStatus);

export const isOutboundReady = (lineStatus: string): boolean => OUTBOUND_READY_STATUSES.includes(lineStatus);

/**
 * Whether the arrival can actually be taken back. The route cannot answer this on its own: a line V1
 * had already inbounded before the mirror ran is JIT + INBOUNDED with neither a reservation nor a
 * ledger row, so nothing knows where its stock sits and the reversal has no location to work from.
 * The server decides and sends `inboundReversible`; an older payload without it falls back to the
 * status check so the button does not vanish mid-rollout.
 */
export const canCancelInbound = (lineStatus: string, inboundReversible?: boolean): boolean =>
  INBOUND_REVERSIBLE_LINE_STATUSES.includes(lineStatus) && inboundReversible !== false;

/** Line states whose goods are in the warehouse or have already left it; the packing label describes only these. */
export const ARRIVED_LINE_STATUSES = ['INBOUNDED', 'READY', 'OUTBOUNDED', 'CLOSED'];
/** Line states the server leaves off the print sheet, so they take no part in deciding whether it prints. */
export const UNPRINTED_LINE_STATUSES = ['CANCELLED', 'RETURNED'];

/**
 * Why the packing label cannot print, or an empty string when it can. It prints once every line the
 * sheet carries has arrived, and stays printable after the order left, because a label is reprinted.
 * A line in any other state, a new one included, waits rather than printing goods nobody can pick.
 */
export const describePackingLabelBlock = (lineStatuses: string[]): string => {
  const printed = lineStatuses.filter((lineStatus) => !UNPRINTED_LINE_STATUSES.includes(lineStatus));
  if (printed.length === 0) {
    return 'No line on this order can be printed';
  }
  return printed.every((lineStatus) => ARRIVED_LINE_STATUSES.includes(lineStatus))
    ? ''
    : 'Every remaining line must be inbounded first';
};
